import { Component, lazy, Suspense, type ReactNode } from "react";
import { useMapConfig } from "../../lib/queries";
import { webglAvailable } from "../../lib/webgl";
import { SEVERITY_COLOR } from "../../lib/format";
import type { Severity } from "../../types/api";

const MiniMapbox = lazy(() => import("./MiniMapbox"));

class MiniFallbackBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

// Rough Australia outline (lon/lat) for the SVG fallback.
const COAST: [number, number][] = [
  [113, -22], [115, -34], [129, -32], [138, -35], [145, -38], [153, -32],
  [153, -25], [143, -14], [130, -12], [122, -18], [114, -22],
];
const LON0 = 111, LON1 = 155, LAT0 = -9, LAT1 = -45, W = 320, H = 180;
const proj = (lon: number, lat: number): [number, number] =>
  [((lon - LON0) / (LON1 - LON0)) * W, ((lat - LAT0) / (LAT1 - LAT0)) * H];

export default function MiniMap({ lat, lng, severity }: { lat: number; lng: number; severity: Severity }) {
  const { data: config } = useMapConfig();
  const useMapbox = config?.has_token && webglAvailable();

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-white/10 bg-ocean-900/60">
      {useMapbox ? (
        <MiniFallbackBoundary fallback={<SvgLocator lat={lat} lng={lng} severity={severity} />}>
          <Suspense fallback={<SvgLocator lat={lat} lng={lng} severity={severity} />}>
            <MiniMapbox lat={lat} lng={lng} severity={severity} />
          </Suspense>
        </MiniFallbackBoundary>
      ) : (
        <SvgLocator lat={lat} lng={lng} severity={severity} />
      )}
      <div className="mono absolute bottom-1 left-2 z-10 text-[9px] text-slatey">
        {lat.toFixed(4)}, {lng.toFixed(4)}
      </div>
    </div>
  );
}

function SvgLocator({ lat, lng, severity }: { lat: number; lng: number; severity: Severity }) {
  const path = COAST.map(([lo, la], i) => { const [x, y] = proj(lo, la); return `${i === 0 ? "M" : "L"}${x.toFixed(0)},${y.toFixed(0)}`; }).join(" ") + " Z";
  const [px, py] = proj(lng, lat);
  const inBounds = px >= 0 && px <= W && py >= 0 && py <= H;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full">
      <path d={path} fill="#12304d" stroke="rgba(34,211,238,0.25)" strokeWidth={1} />
      {inBounds && (
        <g>
          <circle cx={px} cy={py} r={9} fill="none" stroke={SEVERITY_COLOR[severity]} opacity={0.5} className="animate-radar" />
          <circle cx={px} cy={py} r={4} fill={SEVERITY_COLOR[severity]} stroke="#0A1628" strokeWidth={1} />
        </g>
      )}
    </svg>
  );
}
